import { useState } from "react";
import { Heart, MoreHorizontal, Share2 } from "lucide-react";
import type { WorkflowItem } from "@/mocks/workflows";

interface WorkflowCardProps {
  item: WorkflowItem;
  onOpen: (id: string) => void;
}

const subjectColorMap: Record<string, string> = {
  材料科学: "bg-slate-500 text-white",
  生物工程: "bg-teal-600 text-white",
  量子物理: "bg-purple-500 text-white",
  信号处理: "bg-amber-500 text-white",
  催化化学: "bg-indigo-500 text-white",
};

const subjectCoverMap: Record<string, string> = {
  材料科学: "/workflow-covers/materials.jpg",
  生物工程: "/workflow-covers/bio.jpg",
  量子物理: "/workflow-covers/quantum.jpg",
  信号处理: "/workflow-covers/signal.jpg",
  催化化学: "/workflow-covers/catalysis.jpg",
};

function Cover({ type, subject }: { type: WorkflowItem["coverType"]; subject: string }) {
  const img = subjectCoverMap[subject] ?? "/workflow-covers/materials.jpg";
  const baseGradient =
    type === "doc"
      ? "linear-gradient(135deg, rgba(245,246,249,1) 0%, rgba(255,255,255,1) 55%, rgba(247,248,250,1) 100%)"
      : type === "image"
        ? "linear-gradient(135deg, rgba(238,242,255,1) 0%, rgba(245,246,249,1) 55%, rgba(236,253,245,1) 100%)"
        : "linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(251,252,254,1) 55%, rgba(247,248,250,1) 100%)";

  const coverStyle = {
    backgroundImage: `url("${img}"), ${baseGradient}`,
    backgroundSize: "cover",
    backgroundPosition: "center",
  } as const;
  if (type === "image") {
    return (
      <div className="h-full w-full" style={coverStyle} />
    );
  }
  if (type === "doc") {
    return (
      <div className="h-full w-full" style={coverStyle} />
    );
  }
  return (
    <div className="h-full w-full" style={coverStyle} />
  );
}

export default function WorkflowCard({ item, onOpen }: WorkflowCardProps) {
  const [liked, setLiked] = useState(false);
  const tagCls = subjectColorMap[item.subject] ?? "bg-primary text-primary-foreground";

  return (
    <div
      className="group overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
      onClick={() => onOpen(item.id)}
    >
      <div className="relative h-44 overflow-hidden bg-muted">
        <Cover type={item.coverType} subject={item.subject} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent" />
        <div className="absolute bottom-3 left-3 flex items-center gap-2">
          <span className={`inline-block rounded-full px-2.5 py-1 text-[11px] font-medium ${tagCls}`}>{item.subject}</span>
          <span className="inline-block rounded-full bg-white/85 px-2.5 py-1 text-[11px] font-medium text-muted-foreground shadow-sm">
            {item.status}
          </span>
        </div>
      </div>

      <div className="p-4">
        <h3 className="truncate text-sm font-semibold leading-6 tracking-tight text-foreground">{item.title}</h3>
        <p className="mt-2 line-clamp-2 min-h-[40px] text-xs leading-5 text-muted-foreground">{item.description}</p>

        <div className="mt-4 flex items-center justify-between border-t border-border pt-3 text-xs text-muted-foreground">
          <span>{item.updatedAt}</span>
          <div className="flex items-center gap-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
              }}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-accent hover:text-accent-foreground"
              title="分享"
            >
              <Share2 className="h-4 w-4" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLiked(!liked);
              }}
              className={`inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${
                liked ? "text-rose-500" : "hover:bg-accent hover:text-accent-foreground"
              }`}
              title="收藏"
            >
              <Heart className={`h-4 w-4 ${liked ? "fill-current" : ""}`} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
              }}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-accent hover:text-accent-foreground"
              title="更多"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

